import React, { useEffect, useRef } from "react";
import Box from "@mui/material/Box";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Badge from "@mui/material/Badge";
import trayIcon from "../../../assets/icons/tray_vector.svg";
import trapSiteIcon from "../../../assets/icons/trapsite_vector.svg";
import bugReportIcon from "../../../assets/icons/bugreport.svg";
import reportIcon from "../../../assets/icons/report_vector.svg";
import logoutIcon from "../../../assets/icons/logout.svg";
import settingIcon from "../../../assets/icons/setting.svg";
import { useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import ROUTES from "../../AppRouter/routes";
import { useAuth0, useWebServer } from "../../../providers";
import usePolling from "../../../lib/usePollingHook";
import { fetchOrganizations } from "../../../actions";
import { ACTIVITY_REFRESH_INTERVAL_MS } from "../ModalRouter/ActivityMonitor/constants";
import { clampAppEnv } from "../../../utils/environment";
import ConfirmDialog from "../../ConfirmDialog";
import { connect } from "react-redux";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import MonitorHeartIcon from "@mui/icons-material/MonitorHeart";
import WifiIcon from "@mui/icons-material/Wifi";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import RebootButton from "./RebootButton";
import InfoIcon from "@mui/icons-material/Info";
import LightTooltip from "./LightToolTip";
import UserGuideModal from "../SideBar/UserGuides/UserGuideModal";
import { NAV_RAIL_WIDTH } from "./constants";

const NavigationBar = ({
  superUser,
  deviceUuid,
  unreadMaintenanceTotal,
  fetchOrganizations,
}) => {
  const [focus, setFocus] = useState(true);
  const history = useHistory();
  const { pathname } = useLocation();
  const { sendOrganizationRequest } = useWebServer();
  const { logout } = useAuth0();
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [rebootPopup, setRebootPopup] = useState(false);
  const [guideModalOpen, setGuideModalOpen] = useState(false);

  // The superuser organization list - and with it the unread maintenance counts
  // behind the Activity badge - is fetched here rather than in
  // OrganizationSelector, which now only answers "which organization am I acting
  // as" and renders nothing for a single-organization user. NavigationBar is the
  // one app-shell component that is always mounted and it owns the badge.
  //
  // Superusers only. /organization/organizations is the admin list of every
  // organization in the system, and everything it populates - both SuperUser
  // panels, both Activity panels, and the badge below - is superuser-only. A
  // regular user has no surface that reads it, so asking for it would be a
  // request for the whole directory on behalf of someone who renders none of
  // it. Their own organization list comes from /organization/organizations/user
  // via OrganizationSelector instead.
  useEffect(() => {
    if (!superUser) {
      return;
    }
    // A non-background fetch rethrows so the panels that ask for the list can
    // surface the failure. Here the list only feeds the Activity badge, and
    // there is nothing to surface it on - without this an offline load leaves
    // an unhandled rejection behind and the badge simply stays at its last
    // value, which is the intended degradation.
    fetchOrganizations(sendOrganizationRequest, {
      includeUnreadCounts: true,
      env: clampAppEnv(),
    }).catch(() => {});
  }, [fetchOrganizations, sendOrganizationRequest, superUser]);

  const refreshRef = useRef({});
  const refreshingRef = useRef(false);

  useEffect(() => {
    refreshRef.current = { sendOrganizationRequest };
  });

  const refreshCounts = () => {
    if (refreshingRef.current) {
      return;
    }
    refreshingRef.current = true;
    fetchOrganizations(refreshRef.current.sendOrganizationRequest, {
      includeUnreadCounts: true,
      env: clampAppEnv(),
      background: true,
    }).finally(() => {
      refreshingRef.current = false;
    });
  };
  const refreshCountsRef = useRef(refreshCounts);
  useEffect(() => {
    refreshCountsRef.current = refreshCounts;
  });

  // Scoped to the Activity tab rather than the whole session. FETCH_ORGANIZATIONS
  // replaces `organizations` and `orgDevices` wholesale, so polling it everywhere
  // would overwrite the Management panel's optimistic renames, hardware-id edits
  // and org creations - none of which are re-read from the server after the write.
  const onActivityTab = superUser && pathname.includes(ROUTES.activity);

  // Catch up on entering the tab: the badges are otherwise as old as the mount
  // fetch until the first tick, which for a long-running session means opening
  // Activity to visibly stale counts. Only on a transition in - landing on the
  // route directly is already covered by the mount fetch above.
  const wasOnActivityTab = useRef(onActivityTab);
  useEffect(() => {
    if (onActivityTab && !wasOnActivityTab.current) {
      refreshCountsRef.current();
    }
    wasOnActivityTab.current = onActivityTab;
  }, [onActivityTab]);

  usePolling(
    () => refreshCountsRef.current(),
    ACTIVITY_REFRESH_INTERVAL_MS,
    onActivityTab
  );

  // The tab for the section the address is in, by its place in the rail -
  // Management and Activity show only for superusers, Reboot only with a
  // device selected. Guide, Reboot, Report and Logout open dialogs rather than
  // sections, so they are never the selected tab.
  const rail = [
    "trays",
    "collection",
    "reports",
    "guide",
    ...(superUser ? ["management", "activity"] : []),
    ...(deviceUuid && deviceUuid !== "ALLTRAY" ? ["reboot"] : []),
    "internet",
    "settings",
  ];
  const section =
    pathname.startsWith(ROUTES.dataTab) || pathname.startsWith(ROUTES.reports)
      ? "reports"
      : pathname.startsWith(ROUTES.superUserPanel)
      ? "management"
      : pathname.startsWith(ROUTES.activity)
      ? "activity"
      : pathname.startsWith(ROUTES.deviceSetup)
      ? "internet"
      : pathname.startsWith(ROUTES.settings)
      ? "settings"
      : pathname.startsWith(ROUTES.trapSite) || pathname.startsWith(ROUTES.collectionMethods)
      ? "collection"
      : "trays";
  const value = rail.indexOf(section);

  const handleLogoutClick = () => {
    setShowLogoutConfirm(true);
  };

  const onConfirm = () => {
    logout();
  };

  return (
    <>
      <ConfirmDialog
        title={"Logout"}
        children={"Are you sure you want to logout?"}
        open={showLogoutConfirm}
        setOpen={setShowLogoutConfirm}
        onConfirm={onConfirm}
      />
      <Box
        onMouseEnter={() => {
          setFocus(true);
        }}
        onMouseLeave={(e) => {
          e.preventDefault();
          // setFocus(false)
        }}
      >
        <Box
          sx={{
            bgcolor: "background.paper",
            display: "flex",
            height: "100vh",
          }}
        >
          <Tabs
            orientation="vertical"
            value={value}
            sx={{
              borderRight: 1,
              borderColor: "divider",
              // flex: 1,
              ".MuiTabs-flexContainer": {
                display: "flex",
                height: "100vh",
                // flexDirection: "column",
                // justifyContent: "space-between",
              },
              ".MuiButtonBase-root": {
                minWidth: `${NAV_RAIL_WIDTH}px`,
                paddingX: ["8px", "8px", "8px", "12px", "16px", "16px"],
              },
              ".MuiTab-root": {
                marginBottom: "8px",
              },
            }}
          >
            <LightTooltip
              title="Trays"
              onClick={() => {
                history.push(ROUTES.traysTab);
              }}
              size="md"
              open={focus}
            >
              <Tab
                icon={
                  <img
                    className={"w-6"}
                    src={trayIcon}
                    alt="Tray Icon"
                    aria-label="tray-image"
                  ></img>
                }
                onClick={() => {
                  history.push(ROUTES.traysTab);
                }}
                aria-label="trays-tab"
              />
            </LightTooltip>
            <LightTooltip
              title={`Collection`}
              title2={"Details"}
              size="md"
              open={focus}
              onClick={() => {
                history.push(ROUTES.trapSite);
              }}
              slotProps={{
                popper: {
                  modifiers: [
                    {
                      name: "offset",
                      options: {
                        offset: [0, -50],
                      },
                    },
                  ],
                  sx: {
                    padding: 0,
                    zIndex: 0,
                  },
                },
              }}
            >
              <Tab
                icon={
                  <img
                    className={"w-10"}
                    src={trapSiteIcon}
                    alt="Collection Details Icon"
                  />
                }
                onClick={() => {
                  history.push(ROUTES.trapSite);
                }}
                sx={{ paddingBottom: "45px" }}
                aria-label="collection-details-tab"
              />
            </LightTooltip>

            <LightTooltip
              title="Reports"
              size="md"
              open={focus}
              onClick={() => {
                history.push(ROUTES.dataTab);
              }}
            >
              <Tab
                icon={
                  <img
                    className={"w-8"}
                    src={reportIcon}
                    alt="Reports Icon"
                  ></img>
                }
                onClick={() => {
                  history.push(ROUTES.dataTab);
                }}
                aria-label="reports-tab"
              />
            </LightTooltip>

            {/* <Tab
              sx={{ flexGrow: 1 }}
              onMouseOut={(e) => {
                e.preventDefault();
              }}
            ></Tab> */}

            {/* User Guide navigation */}
            <LightTooltip
              title="Guide"
              size="md"
              open={focus}
              onClick={() => {
                setGuideModalOpen(true);
              }}
            >
              <Tab
                // label={focus ? "Settings" : null}
                icon={<InfoIcon sx={{ color: "#167bc3", fontSize: "32px" }} />}
                alt="Guide Icon"
                className={"w-6 w-full"}
                onClick={() => {
                  setGuideModalOpen(true);
                }}
              />
            </LightTooltip>
            {superUser && (
              <LightTooltip
                title="Management"
                size="md"
                open={focus}
                onClick={() => {
                  history.push(ROUTES.superUserPanel);
                }}
              >
                <Tab
                  icon={
                    <AdminPanelSettingsIcon
                      sx={{ color: "status.superuser", fontSize: "32px" }}
                    />
                  }
                  alt="Management Icon"
                  className={"w-full"}
                  onClick={() => {
                    history.push(ROUTES.superUserPanel);
                  }}
                  aria-label="management-tab"
                />
              </LightTooltip>
            )}
            {superUser && (
              <LightTooltip
                title="Activity"
                size="md"
                open={focus}
                onClick={() => {
                  history.push(ROUTES.activity);
                }}
              >
                <Tab
                  icon={
                    <Badge badgeContent={unreadMaintenanceTotal} color="error">
                      <MonitorHeartIcon
                        sx={{ color: "status.superuser", fontSize: "32px" }}
                      />
                    </Badge>
                  }
                  alt="Activity Icon"
                  className={"w-full"}
                  onClick={() => {
                    history.push(ROUTES.activity);
                  }}
                  aria-label="activity-tab"
                />
              </LightTooltip>
            )}
            {deviceUuid && deviceUuid !== "ALLTRAY" && (
              <LightTooltip
                title="Reboot"
                size="md"
                open={focus}
                onClick={() => {
                  setRebootPopup(true);
                }}
              >
                <Tab
                  icon={
                    <RestartAltIcon
                      sx={{ color: "#167bc3", fontSize: "32px" }}
                    />
                  }
                  alt="Reboot"
                  className={"w-full"}
                  onClick={() => setRebootPopup(true)}
                  aria-label="reboot-tab"
                ></Tab>
              </LightTooltip>
            )}
            <LightTooltip
              title={`Internet`}
              title2={"Setup"}
              size="md"
              open={focus}
              onClick={() => {
                history.push(ROUTES.deviceSetup);
              }}
              slotProps={{
                popper: {
                  modifiers: [
                    {
                      name: "offset",
                      options: {
                        offset: [0, -50],
                      },
                    },
                  ],
                  sx: {
                    padding: 0,
                    zIndex: 0,
                  },
                },
              }}
            >
              <Tab
                icon={<WifiIcon sx={{ color: "#167bc3", fontSize: "32px" }} />}
                onClick={() => {
                  history.push(ROUTES.deviceSetup);
                }}
                aria-label="setup-tab"
                sx={{ paddingBottom: "40px" }}
              />
            </LightTooltip>
            <LightTooltip
              title="Settings"
              size="md"
              open={focus}
              onClick={() => {
                history.push(ROUTES.settings);
              }}
            >
              <Tab
                icon={
                  <img
                    className={"w-6"}
                    src={settingIcon}
                    alt="Settings Icon"
                  ></img>
                }
                onClick={() => {
                  history.push(ROUTES.settings);
                }}
                aria-label="settings-tab"
              />
            </LightTooltip>
            <LightTooltip
              title={`Report`}
              title2={"An Issue"}
              // title={
              //   <div style={{ textAlign: "center", cursor: "pointer" }}>
              //     <div>Report</div>
              //     <div>An Issue</div>
              //   </div>
              // }
              size="md"
              open={focus}
              onClick={() => {
                window.open(
                  "https://grove-jumper-74b.notion.site/20e858760a4a8042859fe6703de82af2",
                  "_blank"
                );
              }}
              slotProps={{
                popper: {
                  modifiers: [
                    {
                      name: "offset",
                      options: {
                        offset: [0, -45],
                      },
                    },
                  ],
                  sx: {
                    padding: 0,
                    zIndex: 0,
                  },
                },
              }}
            >
              <Tab
                icon={
                  <img
                    style={{ width: "2rem", height: "2rem", marginTop: "2px" }}
                    src={bugReportIcon}
                    alt="Bug Report Icon"
                  ></img>
                }
                onClick={() => {
                  window.open(
                    "https://grove-jumper-74b.notion.site/20e858760a4a8042859fe6703de82af2",
                    "_blank"
                  );
                }}
                sx={{
                  paddingBottom: "40px",
                  display: "flex",
                }}
              />
            </LightTooltip>
            <LightTooltip
              title="Logout"
              size="md"
              open={focus}
              placement="bottom"
              onClick={handleLogoutClick}
            >
              <Tab
                icon={
                  <img
                    className={"w-10"}
                    src={logoutIcon}
                    alt="Logout Icon"
                  ></img>
                }
                onClick={handleLogoutClick}
                sx={{ marginBottom: "20px" }}
                aria-label="logout-tab"
              />
            </LightTooltip>
          </Tabs>
        </Box>
      </Box>
      {rebootPopup && <RebootButton setModal={setRebootPopup} />}
      {guideModalOpen && (
        <UserGuideModal
          guideModalOpen={guideModalOpen}
          setGuideModalOpen={setGuideModalOpen}
        />
      )}
    </>
  );
};

export const mapStateToProps = (state) => {
  const superUser = state?.user?.superUser || false;
  const selectedDevice = state.devices?.selectedDevice || null;
  const organizations = state?.user?.organizations || {};
  const unreadMaintenanceTotal = Object.values(organizations).reduce(
    (sum, org) => sum + (org?.unreadMaintenanceCount || 0),
    0
  );

  return {
    superUser,
    deviceUuid: selectedDevice,
    unreadMaintenanceTotal,
  };
};

export default connect(mapStateToProps, { fetchOrganizations })(NavigationBar);
